/**
 * @ipc/validation · src/enums/payment-methods.ts — каталог способов оплаты,
 * которые IPC умеет открывать плательщику.
 *
 * `checkout` — страница оплаты Multicard (карты Uzcard/Humo/Visa/Mastercard и
 * всё, что Multicard сам показывает на своей странице). Остальные коды —
 * прямой переход в приложение: `POST /payment` с `payment_system`
 * (docs.multicard.uz «Создание платежа payme/click/uzum и прочие»), без
 * промежуточной страницы Multicard. СБП и банки (Anorbank, Alif, Xazna,
 * Trastpay) — такие же приложения того же списка.
 */
import { z } from 'zod/v4';

/** Схема кода способа оплаты. Порядок — порядок показа по умолчанию. */
export const paymentMethodSchema = z.enum([
  'checkout',
  'sbp',
  'payme',
  'click',
  'uzum',
  'anorbank',
  'alif',
  'oson',
  'xazna',
  'beepul',
  'trastpay',
]);

/** Код способа оплаты. */
export type PaymentMethodCode = z.infer<typeof paymentMethodSchema>;

/** Все коды в порядке показа. */
export const PAYMENT_METHOD_CODES: readonly PaymentMethodCode[] = paymentMethodSchema.options;

/** Способ по умолчанию у нового мерчанта — прежнее поведение (страница Multicard). */
export const DEFAULT_PAYMENT_METHOD: PaymentMethodCode = 'checkout';

/** Подписи для людей (админка, страница выбора, SDK). */
export const PAYMENT_METHOD_LABELS: Readonly<Record<PaymentMethodCode, string>> = {
  checkout: 'Банковская карта',
  sbp: 'СБП',
  payme: 'Payme',
  click: 'Click',
  uzum: 'Uzum',
  anorbank: 'Anorbank',
  alif: 'Alif',
  oson: 'Oson',
  xazna: 'Xazna',
  beepul: 'Beepul',
  trastpay: 'Trastpay',
};

/** true — способ открывается приложением (`POST /payment` с `payment_system`), не страницей Multicard. */
export function isAppPaymentMethod(code: PaymentMethodCode): boolean {
  return code !== 'checkout';
}

/** Сужает произвольную строку до кода способа оплаты. */
export function isPaymentMethodCode(value: unknown): value is PaymentMethodCode {
  return paymentMethodSchema.safeParse(value).success;
}

/**
 * Список способов мерчанта: без дублей, в порядке каталога, только известные коды.
 * Пустой или битый список из БД сводится к способу по умолчанию.
 */
export function normalizePaymentMethods(raw: readonly unknown[] | null | undefined): PaymentMethodCode[] {
  const present = new Set((raw ?? []).filter(isPaymentMethodCode));
  const ordered = PAYMENT_METHOD_CODES.filter((code) => present.has(code));
  return ordered.length > 0 ? ordered : [DEFAULT_PAYMENT_METHOD];
}

/** Непустой список способов без дублей (тело API/формы). */
export const paymentMethodListSchema = z
  .array(paymentMethodSchema)
  .min(1, 'Выберите хотя бы один способ оплаты')
  .max(PAYMENT_METHOD_CODES.length)
  .transform((list) => normalizePaymentMethods(list));
